import { type ReactNode, useEffect, useState } from 'react';
import { Button } from '@/components/primitives/Button';
import { copyToClipboard } from '@/lib/clipboard';
import { errorText, FALLBACK_SUPPORT_CONTACT } from '@/lib/errorCopy';

export function OpenInEmailAction({
  body,
  children,
}: {
  body: string;
  children?: (action: ReactNode, status: ReactNode) => ReactNode;
}): ReactNode {
  const [result, setResult] = useState<{
    body: string;
    kind: 'opening' | 'copied' | 'error';
  } | null>(null);
  useEffect(() => {
    if (result?.kind !== 'opening') return;
    const timer = setTimeout(() => setResult(null), 4000);
    const clearOnAction = (event: MouseEvent): void => {
      if (event.target instanceof Element && event.target.closest('button, a')) setResult(null);
    };
    document.addEventListener('click', clearOnAction, true);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', clearOnAction, true);
    };
  }, [result]);

  const handleOpen = async (): Promise<void> => {
    setResult(null);
    const link = `mailto:?body=${encodeURIComponent(body)}`;
    if (link.length > 1800) {
      try {
        await copyToClipboard(body);
      } catch {
        setResult({ body, kind: 'error' });
        return;
      }
      setResult({ body, kind: 'copied' });
      window.open('mailto:', '_self');
      return;
    }
    window.open(link, '_self');
    setResult({ body, kind: 'opening' });
  };

  const action = (
    <Button variant="primary" onClick={() => void handleOpen()}>
      Open in email
    </Button>
  );
  const status =
    result?.body === body ? (
      <p className="text-xs text-ink-soft" role={result.kind === 'error' ? 'alert' : 'status'}>
        {result.kind === 'opening'
          ? 'Opening your email app. Nothing opened? Use Copy draft email.'
          : result.kind === 'copied'
            ? 'Copied. Paste it into the new message.'
            : errorText('email_open', 'copy_failed', FALLBACK_SUPPORT_CONTACT)}
      </p>
    ) : null;
  return children ? (
    children(action, status)
  ) : (
    <>
      {action}
      {status}
    </>
  );
}
